import { type FormEvent } from "react"
import { Link, useForm } from "@inertiajs/react"
import AppLayout from "@/components/AppLayout"
import PageTitle from "@/components/PageTitle"
import { Button } from "@/components/ui/Button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card"
import { Input } from "@/components/ui/Input"
import { purchase_destinations_path, purchase_products_path } from "@/lib/routes"

interface MarketSettings {
  id: string
  name: string
  import_countries: string[]
  warehouse_notes: string[]
  default_destination: boolean
}

interface Props {
  markets: MarketSettings[]
  seen: {
    import_countries: Record<string, number>
    warehouse_notes: Record<string, number>
    blank_both: number
  }
}

function seenList(counts: Record<string, number>): string {
  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1])
  return entries.length === 0 ? "none" : entries.map(([text, count]) => `${text} (${count})`).join(", ")
}

// Tells the Inventory status and the Purchase Plan which market an incoming order is for, using what the
// buyers type in the shipping schedule's "Import Country" and "Note / KHO" columns.
export default function PurchaseDestinations({ markets, seen }: Props) {
  const form = useForm({
    default_market_id: markets.find((m) => m.default_destination)?.id ?? "",
    markets: Object.fromEntries(
      markets.map((m) => [
        m.id,
        { import_countries: m.import_countries.join(", "), warehouse_notes: m.warehouse_notes.join(", ") },
      ])
    ),
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    form.patch(purchase_destinations_path(), { preserveScroll: true })
  }

  function setField(marketId: string, field: "import_countries" | "warehouse_notes", value: string) {
    form.setData("markets", { ...form.data.markets, [marketId]: { ...form.data.markets[marketId], [field]: value } })
  }

  return (
    <AppLayout
      header={
        <PageTitle
          title="Incoming order destinations"
          subtitle="Which market each incoming order in the buyers' shipping schedule is for."
          subtitleExtra={<Link href={purchase_products_path()} className="text-link">Back to Purchase Plan</Link>}
        />
      }
    >
      <form onSubmit={submit} className="flex max-w-2xl flex-col gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">On orders still on their way</CardTitle>
            <CardDescription>What the buyers typed. Every entry needs a market below.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-1 text-sm">
            <div><span className="font-semibold">Import Country: </span>{seenList(seen.import_countries)}</div>
            <div><span className="font-semibold">Note / KHO: </span>{seenList(seen.warehouse_notes)}</div>
            <div><span className="font-semibold">Both blank: </span>{seen.blank_both} order lines</div>
          </CardContent>
        </Card>

        {markets.map((market) => (
          <Card key={market.id}>
            <CardHeader>
              <CardTitle className="text-base">{market.name}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-semibold">Import Country values, separated by commas</span>
                <Input
                  value={form.data.markets[market.id].import_countries}
                  onChange={(event) => setField(market.id, "import_countries", event.target.value)}
                  placeholder="For example: US, SING"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-semibold">Warehouse names in Note / KHO, separated by commas</span>
                <Input
                  value={form.data.markets[market.id].warehouse_notes}
                  onChange={(event) => setField(market.id, "warehouse_notes", event.target.value)}
                  placeholder="Used when Import Country is blank"
                />
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="default_market_id"
                  checked={form.data.default_market_id === market.id}
                  onChange={() => form.setData("default_market_id", market.id)}
                />
                Orders that match nothing are assumed to be for {market.name}
              </label>
            </CardContent>
          </Card>
        ))}

        <div>
          <Button type="submit" disabled={form.processing}>{form.processing ? "Saving…" : "Save"}</Button>
        </div>
      </form>
    </AppLayout>
  )
}
